import React, { useRef } from 'react';
import { FileText, Download, BookOpen, GraduationCap, Layers, ListOrdered } from 'lucide-react';
import { AIOutput } from '@/lib/gemini';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { formatLatexString, sanitizeLatex } from '@/lib/latexUtils';
import { StructuredSolutionArea } from './StructuredSolutionArea';
import { isValidRenderableImageUrl } from '@/lib/visualUtils';

import { ErrorBoundary } from './ErrorBoundary';

interface NotesSectionProps {
  data: AIOutput;
}

export function NotesSection({ data }: NotesSectionProps) {
  const notesRef = useRef<HTMLDivElement>(null);
  const stepsContainerRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [failedImages, setFailedImages] = React.useState<Set<string>>(new Set());

  const handleImageError = (url: string) => {
    setFailedImages(prev => {
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  };

  const handleDownload = async () => {
    if (!notesRef.current || isDownloading) return;
    
    try {
      setIsDownloading(true);
      console.log("PDF Generation: START Step-by-Step Capture");
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - (2 * margin);
      
      // Identify all sections to capture
      const sections = notesRef.current.querySelectorAll('.pdf-step-item');
      if (sections.length === 0) throw new Error("No capture sections found.");

      let firstPage = true;
      let currentY = margin;

      for (let i = 0; i < sections.length; i++) {
        const el = sections[i] as HTMLElement;
        
        // Capture each section independently
        const canvas = await html2canvas(el, {
          scale: 2, // High quality for screenshots
          useCORS: true,
          backgroundColor: '#0f172a',
          logging: false,
          onclone: (clonedDoc) => {
            // DEEP NEUTRALIZATION of modern CSS in the clone
            const styleTags = clonedDoc.querySelectorAll('style');
            styleTags.forEach(tag => {
              if (tag.innerHTML.includes('oklch')) {
                tag.innerHTML = tag.innerHTML.replace(/(oklch|oklab|lab|lch)\([^)]+\)/g, '#64748b');
                tag.innerHTML = tag.innerHTML.replace(/--[a-z0-9-]+:\s*(oklch|oklab)[^;!]+(!important)?;/gi, '--sanitized: #000;');
              }
            });

            const all = clonedDoc.getElementsByTagName('*');
            for (let j = 0; j < all.length; j++) {
              const node = all[j] as HTMLElement;
              try {
                const s = window.getComputedStyle(node);
                ['color', 'backgroundColor', 'borderColor', 'fill', 'stroke'].forEach(p => {
                  const val = (s as any)[p];
                  if (val && val.includes('oklch')) {
                    let fb = '#f8fafc';
                    if (p === 'backgroundColor') fb = '#0f172a';
                    if (p === 'borderColor') fb = '#334155';
                    node.style.setProperty(p, fb, 'important');
                  }
                });
                node.style.setProperty('boxShadow', 'none', 'important');
                node.style.setProperty('textShadow', 'none', 'important');
                node.style.setProperty('filter', 'none', 'important');
                node.style.setProperty('transition', 'none', 'important');
              } catch (e) {}
            }
          }
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.9);
        const imgProps = pdf.getImageProperties(imgData);
        const imgH = (imgProps.height * contentWidth) / imgProps.width;

        // Check if we need a new page
        if (!firstPage && (currentY + imgH > pageHeight - margin)) {
          pdf.addPage();
          currentY = margin;
        }

        if (firstPage) firstPage = false;

        // If a single section is larger than an entire page (very rare but possible)
        if (imgH > pageHeight - 2 * margin) {
           pdf.addImage(imgData, 'JPEG', margin, currentY, contentWidth, pageHeight - 2 * margin);
           currentY = pageHeight; // Force next page
        } else {
           pdf.addImage(imgData, 'JPEG', margin, currentY, contentWidth, imgH);
           currentY += imgH + 5; // Add spacing between sections
        }
      }

      const docTitle = (data.topic || 'Solution').replace(/[^a-z0-9]/gi, '_');
      pdf.save(`ProfessorX_Notes_${docTitle}.pdf`);
      console.log("PDF: SUCCESS");
      setIsDownloading(false);

    } catch (error) {
      console.error("PDF Generation Failed:", error);
      setIsDownloading(false);
      const msg = error instanceof Error ? error.message : "Error";
      if (msg.includes('oklch') || msg.includes('parse')) {
        window.print();
      } else {
        const confirmed = window.confirm(`PDF Download Error (${msg}). Open Print Dialog to save as PDF?`);
        if (confirmed) window.print();
      }
    }
  };

  const preprocessContent = (content: string) => {
    if (!content) return '';
    return sanitizeLatex(content);
  };

  const formatLatex = (content: string, inline: boolean = false) => {
    return formatLatexString(content, inline);
  };

  return (
    <div ref={notesRef} className="w-full bg-slate-900 rounded-2xl shadow-sm border border-slate-800 overflow-hidden mt-8 max-w-full notes-printable-area">
      <div className="bg-slate-800/50 px-6 py-4 border-b border-slate-800 flex items-center justify-between no-print no-screenshot">
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-indigo-400" />
          <h2 className="text-lg font-semibold text-slate-100">Study Notes</h2>
        </div>
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
            isDownloading 
              ? 'bg-slate-800 text-slate-500 cursor-not-allowed' 
              : 'text-indigo-400 hover:text-indigo-300 bg-indigo-900/30'
          }`}
        >
          {isDownloading ? (
            <>
              <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
              Generating PDF...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Download PDF
            </>
          )}
        </button>
      </div>

      <div className="p-8 space-y-8">
        <div className="border-b border-slate-800 pb-8 pdf-step-item">
          <div className="flex gap-2 mb-6">
            <span className="bg-indigo-900/50 text-indigo-300 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              {data.subject}
            </span>
            {(data as any).is_study_materials_mode && (
              <span className="bg-amber-500/20 text-amber-300 border border-amber-400/30 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Exam Prep Mode
              </span>
            )}
          </div>

          {(data as any).study_materials_summary && (
            <div className="mb-6 bg-gradient-to-r from-indigo-950/70 via-purple-950/50 to-slate-900 border border-indigo-500/30 rounded-2xl p-5 text-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-amber-400 text-slate-950 text-[10px] uppercase font-black px-2.5 py-0.5 rounded-full">
                  Material Grounded Masterclass
                </span>
                <span className="text-xs text-indigo-300 font-bold">
                  {(data as any).study_materials_summary.total_documents} Sources Analyzed
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="font-bold text-amber-300 mb-1">High Priority Exam Topics:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-slate-300">
                    {(data as any).study_materials_summary.high_priority_topics?.map((t: string, idx: number) => (
                      <li key={idx}>{t}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-bold text-indigo-300 mb-1">Source Priority & Hierarchy:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-slate-300">
                    {(data as any).study_materials_summary.documents?.map((d: any, idx: number) => (
                      <li key={idx}>
                        {d.fileName} <span className="text-[10px] text-indigo-300 font-bold">[{d.role}]</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          <h3 className="text-2xl font-montserrat font-bold text-slate-100 mb-4 tracking-tight">Question</h3>
          <div className="text-slate-300 text-lg leading-snug font-montserrat font-bold bg-slate-800/50 p-6 rounded-2xl border border-slate-700 shadow-sm mb-6">
            <ErrorBoundary fallback={<span>{data.question}</span>}>
              <Latex strict={false}>{preprocessContent(data.question)}</Latex>
            </ErrorBoundary>
          </div>
          
          {data.static_image_url && isValidRenderableImageUrl(data.static_image_url) && !failedImages.has(data.static_image_url) && (
            <div className="mb-6 bg-slate-950 p-4 rounded-2xl border border-slate-800 shadow-inner overflow-hidden max-w-2xl mx-auto">
              <img 
                src={data.static_image_url} 
                alt="Diagram" 
                className="w-full h-auto rounded-xl object-contain max-h-[400px]"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
                onError={() => handleImageError(data.static_image_url!)}
              />
              <p className="mt-4 text-slate-500 text-xs font-bold text-center uppercase tracking-widest">
                Source: Reference Visual
              </p>
            </div>
          )}

          {data.diagram_needed && data.diagram_description && (
            <div className="mt-4 flex items-center gap-2 text-indigo-400 text-sm font-bold font-montserrat">
              <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
              VISUAL CONTEXT: {data.diagram_description}
            </div>
          )}
        </div>

        <div ref={stepsContainerRef}>
          <h3 className="text-xl font-montserrat font-bold text-slate-100 mb-6 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-400" />
            Step-by-Step Solution
          </h3>
          <div className="space-y-6">
            {data.steps.map((step, idx) => {
              const stepImgUrl = (step as any).image_url || (step as any).static_image_url;
              const hasValidStepImg = isValidRenderableImageUrl(stepImgUrl) && !failedImages.has(stepImgUrl);

              return (
                <div key={idx} className="flex gap-6 pdf-step-item p-4 rounded-2xl transition-colors hover:bg-slate-800/20">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-900/50 flex items-center justify-center text-indigo-300 font-bold">
                    {step.step}
                  </div>
                  <div className="flex-1 bg-slate-800/50 rounded-xl p-6 border border-slate-700 text-left overflow-hidden">
                    {data.diagram_needed && step.diagram_description && (
                      <div className="mb-2 text-indigo-400 text-xs font-bold uppercase tracking-widest opacity-80 font-montserrat">
                        {step.diagram_description}
                      </div>
                    )}
                    <div className="mb-4 w-full">
                      <ErrorBoundary fallback={<span className="text-red-400 text-sm">Error rendering math</span>}>
                        <StructuredSolutionArea step={step} stepIndex={idx} fontSizeClass="text-2xl md:text-3xl" lineGapClass="gap-3" />
                      </ErrorBoundary>
                    </div>
                    
                    {data.diagram_needed && step.diagram_svg && (
                      <div className="mb-6 bg-white/[0.03] p-6 rounded-2xl border border-white/5 shadow-inner max-w-lg mx-auto overflow-hidden">
                        <div 
                          className="w-full h-auto flex items-center justify-center [&>svg]:max-w-full [&>svg]:h-auto"
                          dangerouslySetInnerHTML={{ __html: step.diagram_svg }}
                        />
                      </div>
                    )}

                    {hasValidStepImg && !step.diagram_svg && (
                      <div className="mb-6 bg-slate-950 p-4 rounded-2xl border border-slate-800 shadow-inner max-w-lg mx-auto overflow-hidden">
                        <img 
                          src={stepImgUrl} 
                          alt={step.diagram_description || "Step Visual"} 
                          className="w-full h-auto rounded-xl object-contain max-h-[300px]"
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                          onError={() => handleImageError(stepImgUrl)}
                        />
                      </div>
                    )}

                  <div className="text-slate-400 italic border-l-4 border-indigo-800 pl-4 whitespace-normal break-words overflow-visible leading-[1.6] tracking-[0.2px] font-montserrat font-bold bg-indigo-500/5 p-4 rounded-r-xl">
                    <ErrorBoundary fallback={<span className="text-red-400 text-sm">Error rendering math</span>}>
                      <Latex strict={false}>{`"${preprocessContent(typeof step.explanation === 'string' ? step.explanation : '')}"`}</Latex>
                    </ErrorBoundary>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-6 pdf-step-item p-4">
          {/* OneShot Specific Sections */}
          {(data as any).is_oneshot_mode && (
            <>
              {/* Structured 6-Module Roadmap Card */}
              {(data as any).roadmap && (data as any).roadmap.length > 0 && (
                <div className="bg-slate-900/90 rounded-2xl p-6 border border-indigo-700/60 overflow-hidden mb-6 space-y-4">
                  <div className="flex items-center justify-between gap-3 border-b border-indigo-800/60 pb-3">
                    <h3 className="text-xl font-montserrat font-bold text-indigo-300 flex items-center gap-2">
                      <Layers className="w-6 h-6 text-indigo-400" />
                      Structured 6-Chapter Masterclass Roadmap
                    </h3>
                    <span className="text-xs bg-indigo-900/80 border border-indigo-600 text-indigo-200 px-3 py-1 rounded-full uppercase font-bold tracking-wider">
                      0:00 - 20:00 Timeline
                    </span>
                  </div>

                  <p className="text-xs text-slate-300 leading-relaxed font-sans">
                    Designed for zero-knowledge mastery: 15 minutes of progressive concept teaching across 6 core modules, followed by a 5-minute exam question solving session.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
                    {(data as any).roadmap.map((item: any, idx: number) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-xl border flex flex-col justify-between transition-all ${
                          item.module_number === 7 || item.phase?.includes('Important')
                            ? 'bg-purple-950/40 border-purple-700/60 text-purple-200'
                            : 'bg-slate-800/60 border-slate-700/80 text-slate-200 hover:border-indigo-500/50'
                        }`}
                      >
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                              item.module_number === 7 || item.phase?.includes('Important')
                                ? 'bg-purple-900/80 text-purple-300 border border-purple-600'
                                : 'bg-indigo-900/80 text-indigo-300 border border-indigo-700'
                            }`}>
                              {item.module_number ? `Module ${item.module_number}` : `Part ${idx + 1}`}
                            </span>
                            <span className="text-[10px] font-mono text-slate-400">
                              Steps {item.step_start} - {item.step_end}
                            </span>
                          </div>
                          <h4 className="text-sm font-bold text-white mb-1 leading-snug">
                            {item.title}
                          </h4>
                          {item.description && (
                            <p className="text-xs text-slate-300 leading-normal line-clamp-2">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Master Faculty Lecture & Concept Breakdown Section */}
              {(data as any).master_faculty_explanation && (
                <div className="bg-slate-900/90 rounded-2xl p-6 border border-indigo-700/60 overflow-hidden mb-6 space-y-6">
                  <div className="flex items-center justify-between gap-3 border-b border-indigo-800/60 pb-4">
                    <h3 className="text-xl font-montserrat font-bold text-indigo-300 flex items-center gap-2">
                      <GraduationCap className="w-6 h-6 text-indigo-400" />
                      Master Faculty Concept Explanation
                    </h3>
                    <span className="text-xs bg-indigo-900/60 border border-indigo-700 text-indigo-200 px-3 py-1 rounded-full uppercase font-bold">
                      Professor X Masterclass
                    </span>
                  </div>

                  {/* Overview & Intuition */}
                  {(data as any).master_faculty_explanation.overview_and_intuition && (
                    <div className="bg-slate-800/60 p-5 rounded-xl border border-slate-700">
                      <h4 className="text-sm font-bold uppercase tracking-wider text-indigo-400 mb-2">Overview & Real-World Intuition</h4>
                      <p className="text-sm text-slate-200 leading-relaxed font-sans">
                        {(data as any).master_faculty_explanation.overview_and_intuition}
                      </p>
                    </div>
                  )}

                  {/* Core Theories & Reasoning */}
                  {(data as any).master_faculty_explanation.core_theories_and_reasoning && (data as any).master_faculty_explanation.core_theories_and_reasoning.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold uppercase tracking-wider text-indigo-400">Core Principles & Academic Reasoning</h4>
                      <div className="space-y-2">
                        {(data as any).master_faculty_explanation.core_theories_and_reasoning.map((theory: string, idx: number) => (
                          <div key={idx} className="bg-slate-800/40 p-4 rounded-xl border border-slate-700/60 text-sm text-slate-200 leading-relaxed flex gap-3 items-start">
                            <span className="bg-indigo-900/80 text-indigo-300 font-bold px-2 py-0.5 rounded text-xs mt-0.5">{idx + 1}</span>
                            <div className="flex-1">
                              <ErrorBoundary fallback={<span>{theory}</span>}>
                                <Latex strict={false}>{preprocessContent(theory)}</Latex>
                              </ErrorBoundary>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Worked Examples */}
                  {(data as any).master_faculty_explanation.worked_examples_and_cases && (data as any).master_faculty_explanation.worked_examples_and_cases.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold uppercase tracking-wider text-emerald-400">Worked Examples & Step-by-Step Derivations</h4>
                      <div className="grid grid-cols-1 gap-4">
                        {(data as any).master_faculty_explanation.worked_examples_and_cases.map((ex: any, idx: number) => (
                          <div key={idx} className="bg-emerald-950/20 p-5 rounded-xl border border-emerald-800/40 space-y-3">
                            <div className="text-sm font-bold text-emerald-300">{ex.example_title || `Worked Example ${idx + 1}`}</div>
                            <div className="text-xs font-semibold text-slate-200 bg-slate-900/80 p-3 rounded-lg border border-slate-800">
                              <strong>Problem:</strong> {ex.problem}
                            </div>
                            <div className="text-xs text-slate-300 leading-relaxed whitespace-pre-line">
                              <strong>Step-by-Step Working:</strong>
                              <div className="mt-1">
                                <ErrorBoundary fallback={<span>{ex.step_by_step_working}</span>}>
                                  <Latex strict={false}>{preprocessContent(ex.step_by_step_working)}</Latex>
                                </ErrorBoundary>
                              </div>
                            </div>
                            {ex.conclusion && (
                              <div className="text-xs text-emerald-400 font-semibold">
                                <strong>Conclusion:</strong> {ex.conclusion}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Summary & Synthesis */}
                  {(data as any).master_faculty_explanation.summary_and_synthesis && (
                    <div className="bg-indigo-950/40 p-4 rounded-xl border border-indigo-800/40 text-xs text-indigo-200 leading-relaxed">
                      <strong>Professor X Synthesis:</strong> {(data as any).master_faculty_explanation.summary_and_synthesis}
                    </div>
                  )}
                </div>
              )}

              {/* Concept Map Section */}
              {(data as any).revision_section?.concept_map && (data as any).revision_section.concept_map.length > 0 && (
                <div className="bg-indigo-950/50 rounded-2xl p-6 border border-indigo-800/80 overflow-hidden mb-6">
                  <h3 className="text-xl font-montserrat font-bold text-indigo-200 mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-indigo-500 animate-pulse" />
                    Chapter Concept Map
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(data as any).revision_section.concept_map.map((item: any, idx: number) => (
                      <div key={idx} className="bg-slate-800/80 p-4 rounded-xl border border-slate-700">
                        <div className="font-bold text-indigo-300 text-base mb-2">{item.concept}</div>
                        <div className="text-xs text-slate-300 mb-2">{item.relationship}</div>
                        {item.connects_to && item.connects_to.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {item.connects_to.map((conn: string, cIdx: number) => (
                              <span key={cIdx} className="bg-indigo-900/60 text-indigo-200 border border-indigo-700/50 text-[11px] font-semibold px-2 py-0.5 rounded-lg">
                                → {conn}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* OneShot Formula Sheet */}
              {(data as any).revision_section?.formula_sheet && (data as any).revision_section.formula_sheet.length > 0 && (
                <div className="bg-amber-950/40 rounded-2xl p-6 border border-amber-800/80 overflow-hidden mb-6">
                  <h3 className="text-xl font-montserrat font-bold text-amber-200 mb-4">Master Formula Sheet</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(data as any).revision_section.formula_sheet.map((item: any, idx: number) => (
                      <div key={idx} className="bg-slate-900/90 p-4 rounded-xl border border-amber-800/40">
                        <div className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-1">{item.name}</div>
                        <div className="text-lg font-bold text-white mb-2 overflow-x-auto">
                          <ErrorBoundary fallback={<span>{item.formula}</span>}>
                            <Latex strict={false}>{formatLatex(item.formula, true)}</Latex>
                          </ErrorBoundary>
                        </div>
                        <div className="text-xs text-slate-300">{item.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 5-Min Important Questions & Detailed Solutions */}
              {(data as any).important_questions && (data as any).important_questions.length > 0 && (
                <div className="bg-purple-950/40 rounded-2xl p-6 border border-purple-800/80 overflow-hidden mb-6">
                  <h3 className="text-xl font-montserrat font-bold text-purple-200 mb-6 flex items-center justify-between">
                    <span>Important Exam Questions & Solutions (5-Min Session)</span>
                    <span className="text-xs bg-purple-900/60 border border-purple-700 text-purple-300 px-3 py-1 rounded-full uppercase font-bold">
                      {(data as any).important_questions.length} Questions
                    </span>
                  </h3>
                  <div className="space-y-6">
                    {(data as any).important_questions.map((q: any, idx: number) => (
                      <div key={idx} className="bg-slate-900/90 p-6 rounded-2xl border border-slate-800 space-y-4">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <span className="text-sm font-bold text-purple-300 uppercase tracking-wider">
                            Q{q.id || idx + 1}. {q.type || 'Exam Question'}
                          </span>
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase ${
                            q.difficulty === 'Hard' ? 'bg-red-900/60 text-red-300 border border-red-700' :
                            q.difficulty === 'Medium' ? 'bg-amber-900/60 text-amber-300 border border-amber-700' :
                            'bg-emerald-900/60 text-emerald-300 border border-emerald-700'
                          }`}>
                            {q.difficulty || 'Standard'}
                          </span>
                        </div>

                        <div className="text-white text-base font-bold bg-slate-800/80 p-4 rounded-xl border border-slate-700">
                          <ErrorBoundary fallback={<span>{q.question}</span>}>
                            <Latex strict={false}>{preprocessContent(q.question)}</Latex>
                          </ErrorBoundary>
                        </div>

                        {q.think_about_this && (
                          <div className="text-xs text-indigo-300 bg-indigo-950/40 p-3 rounded-xl border border-indigo-800/40">
                            <strong>Think About This:</strong> {q.think_about_this}
                          </div>
                        )}

                        {q.approach && (
                          <div className="text-xs text-slate-300">
                            <strong>Approach:</strong> {q.approach}
                          </div>
                        )}

                        {q.answer && (
                          <div className="text-sm font-bold text-emerald-300 bg-emerald-950/40 p-4 rounded-xl border border-emerald-800/50">
                            <strong>Solution:</strong>
                            <div className="mt-1 text-white">
                              <ErrorBoundary fallback={<span>{q.answer}</span>}>
                                <Latex strict={false}>{preprocessContent(q.answer)}</Latex>
                              </ErrorBoundary>
                            </div>
                          </div>
                        )}

                        {q.shortcut_key_insight && (
                          <div className="text-xs text-amber-300 bg-amber-950/30 p-3 rounded-xl border border-amber-800/30">
                            <strong>Key Insight / Shortcut:</strong> {q.shortcut_key_insight}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Smart Recommendations */}
              {(data as any).smart_recommendations && (
                <div className="bg-slate-800/80 rounded-2xl p-6 border border-slate-700 overflow-hidden mb-6">
                  <h3 className="text-lg font-montserrat font-bold text-slate-100 mb-3">Smart Recommendations for Next Steps</h3>
                  <div className="space-y-2 text-xs text-slate-300">
                    <div><strong>Recommended Next Chapter:</strong> {(data as any).smart_recommendations.next_chapter}</div>
                    <div><strong>Revision Plan:</strong> {(data as any).smart_recommendations.revision_plan}</div>
                  </div>
                </div>
              )}
            </>
          )}

          {data.formulas && data.formulas.length > 0 && !(data as any).is_oneshot_mode && (
            <div className="bg-amber-900/30 rounded-xl p-6 border border-amber-800 overflow-hidden">
              <h3 className="text-lg font-montserrat font-bold text-amber-100 mb-4">Key Formulas Used</h3>
              <ul className="space-y-3">
                {data.formulas.map((formula, idx) => (
                  <li key={idx} className="text-white font-montserrat font-bold overflow-x-auto whitespace-normal break-words leading-[1.6] tracking-[0.2px]">
                    <ErrorBoundary fallback={<span className="text-red-400 text-sm">Error rendering math</span>}>
                      <Latex strict={false}>{formatLatex(typeof formula === 'string' ? formula : '', true)}</Latex>
                    </ErrorBoundary>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.final_answer && (
            <div className="bg-emerald-900/30 rounded-xl p-6 border border-emerald-800 overflow-hidden">
              <h3 className="text-lg font-montserrat font-bold text-emerald-100 mb-4">Final Answer</h3>
              <div className="text-white font-montserrat font-bold text-3xl whitespace-normal break-words overflow-x-auto leading-[1.6] tracking-[0.2px] space-y-2">
                {data.final_answer.split('[BR]').map((line, lIdx) => (
                  <div key={lIdx}>
                    <ErrorBoundary fallback={<span className="text-red-400 text-sm">Error rendering math</span>}>
                      <Latex strict={false}>{formatLatex(line.trim())}</Latex>
                    </ErrorBoundary>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
